import { useState } from 'react';
import { Package, DollarSign, TrendingUp, Percent } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { useDashboardData } from '../hooks/useDashboardData';

// Dashboard Components
import MetricCard from '../components/dashboard/MetricCard';
import FilterPanel from '../components/dashboard/FilterPanel';
import TariffTrendChart from '../components/dashboard/TariffTrendChart';
import OemBreakdownChart from '../components/dashboard/OemBreakdownChart';
import TopPartsTable from '../components/dashboard/TopPartsTable';
import RecentActivity from '../components/dashboard/RecentActivity';
import QuickActions from '../components/dashboard/QuickActions';
import InsightsPanel from '../components/dashboard/InsightsPanel';

export default function Dashboard() {
  const [filters, setFilters] = useState({});
  const { data, isLoading, error, refetch } = useDashboardData(filters);

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
  };

  const handleOemClick = (oem) => {
    setFilters({ ...filters, oem });
  };

  // Extract data safely
  const metrics = data?.data?.metrics || {};
  const trends = data?.data?.trends || {};
  const oemBreakdown = data?.data?.oem_breakdown || [];
  const atRiskParts = data?.data?.at_risk_parts || [];
  const trendData = data?.data?.trend_data || [];

  // Get list of OEMs for filter
  const oems = oemBreakdown.map(item => item.oem);

  // Format currency
  const formatCurrency = (cents) => {
    if (!cents || cents === 0) return '$0';
    const value = cents / 100;
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  };

  // Format number
  const formatNumber = (num) => {
    if (!num || num === 0) return '0';
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toLocaleString('en-US');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Strategic Dashboard</h1>
          <p className="mt-2 text-gray-600">
            Portfolio-wide tariff impact analysis & recent activity
          </p>
        </div>
        <div className="text-sm text-gray-500">
          Last updated: {new Date().toLocaleTimeString()}
        </div>
      </div>

      {/* Filter Panel */}
      <FilterPanel onApplyFilters={handleApplyFilters} oems={oems} />

      {/* Error State */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>
            Unable to load dashboard data. 
            <Button variant="link" onClick={() => refetch()} className="ml-2">
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Metrics Cards */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Total Parts"
          value={formatNumber(metrics.total_parts)}
          trend={{
            value: trends.parts_trend || 0,
            direction: trends.parts_trend > 0 ? 'up' : 'down',
            label: 'vs last quarter'
          }}
          status="default"
          icon={Package}
          loading={isLoading}
        />
        <MetricCard
          title="Contract Value"
          value={formatCurrency(metrics.total_value_cents)}
          trend={{
            value: trends.value_trend || 0,
            direction: 'up',
            label: 'vs last quarter'
          }}
          status="success"
          icon={DollarSign}
          loading={isLoading}
        />
        <MetricCard
          title="Total Tariff Exposure"
          value={formatCurrency(metrics.total_tariff_cents)}
          trend={{
            value: trends.tariff_trend || 0,
            direction: 'up',
            label: 'vs last quarter'
          }}
          status="warning"
          icon={TrendingUp}
          loading={isLoading}
        />
        <MetricCard
          title="Effective Tariff Rate"
          value={`${(metrics.effective_rate || 0).toFixed(1)}%`}
          trend={{
            value: 0,
            direction: 'neutral',
            label: 'Portfolio average'
          }}
          status={metrics.effective_rate > 5 ? 'warning' : 'success'}
          icon={Percent}
          loading={isLoading}
        />
      </div>

      {/* Main Content Grid - 2 Columns on Large Screens */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left Column - 2/3 width */}
        <div className="lg:col-span-2 space-y-6">
          {/* Trend Chart - Full Width */}
          <TariffTrendChart data={trendData} loading={isLoading} />

          {/* Charts Grid */}
          <div className="grid gap-6 lg:grid-cols-2">
            <OemBreakdownChart 
              data={oemBreakdown} 
              loading={isLoading}
              onOemClick={handleOemClick}
            />
            <TopPartsTable 
              parts={atRiskParts.slice(0, 5)} 
              loading={isLoading}
            />
          </div>
        </div>

        {/* Right Column - 1/3 width */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <QuickActions />

          {/* Recent Activity */}
          <RecentActivity />
        </div>
      </div>

      {/* Insights Panel */}
      {!isLoading && data?.success && (
        <InsightsPanel 
          metrics={metrics} 
          oemBreakdown={oemBreakdown}
          atRiskParts={atRiskParts}
        />
      )}
    </div>
  );
}
